import { useCallback } from "react";
import { useOverlayPosition } from "./useOverlayPosition";
import { isNil, mergeProps, useControllableState, useEventCallback, useFocusManager, useFocusScope, useId, useMergedRefs } from "../../shared";
import { isTargetParent } from "./isTargetParent";
import { useOverlayTrigger } from "./useOverlayTrigger";
import { usePopupLightDismiss } from "./usePopupLightDismiss";
import { useRestoreFocus } from "./useRestoreFocus";
export function usePopup(type, {
  allowFlip = true,
  allowPreventOverflow = true,
  boundaryElement,
  defaultOpen,
  disabled,
  hasArrow,
  hideOnEscape = true,
  hideOnLeave = true,
  hideOnOutsideClick,
  id,
  keyProp,
  offset,
  onOpenChange,
  open,
  position,
  restoreFocus = true,
  trigger = "click"
} = {}) {
  const [isOpen, setIsOpen] = useControllableState(open, defaultOpen, false);
  const [focusScope, setFocusRef] = useFocusScope();
  const updateIsOpen = useCallback((event, newValue) => {
    if (isOpen !== newValue) {
      setIsOpen(newValue);

      if (!isNil(onOpenChange)) {
        onOpenChange(event, newValue);
      }
    }
  }, [onOpenChange, isOpen, setIsOpen]);
  const triggerProps = useOverlayTrigger(isOpen, {
    hideOnLeave,
    isDisabled: disabled,
    onHide: useEventCallback(event => {
      // Prevent from closing when the focus goes to an element of the overlay on opening.
      if (!isTargetParent(event.relatedTarget, overlayRef)) {
        updateIsOpen(event, false);
      }
    }),
    onShow: useEventCallback(event => {
      updateIsOpen(event, true);
    }),
    trigger
  });
  const {
    arrowRef,
    overlayRef: overlayPositionRef,
    triggerRef
  } = useOverlayPosition({
    allowFlip,
    allowPreventOverflow,
    boundaryElement,
    hasArrow,
    offset,
    position
  });
  const overlayRef = useMergedRefs(overlayPositionRef, setFocusRef);
  const overlayDismissProps = usePopupLightDismiss(triggerRef, overlayRef, {
    hideOnEscape,
    hideOnLeave,
    hideOnOutsideClick,
    onHide: useEventCallback(event => {
      updateIsOpen(event, false);
    }),
    trigger
  });
  const focusManager = useFocusManager(focusScope, {
    keyProp
  });
  const restoreFocusProps = useRestoreFocus(focusScope, {
    isDisabled: !restoreFocus || !isOpen
  });
  const overlayId = useId(id, "o-ui-overlay");
  return {
    arrowProps: !hasArrow ? {} : {
      ref: arrowRef
    },
    focusManager,
    focusScope,
    isOpen,
    overlayProps: mergeProps({
      id: overlayId,
      ref: overlayRef,
      show: isOpen
    }, overlayDismissProps, restoreFocusProps),
    setIsOpen: updateIsOpen,
    triggerProps: mergeProps({
      "aria-controls": isOpen ? overlayId : undefined,
      "aria-expanded": isOpen,
      "aria-haspopup": type,
      ref: triggerRef,
      tabIndex: !restoreFocus && isOpen ? -1 : undefined
    }, triggerProps)
  };
}